// Tickets — lista de todos los lugares con info de ticket + estado
// Ordenado en 3 secciones por prioridad:
//  1. Sí o sí: comprar antes (status="advance")
//  2. Quizás convenga (mixto): puede pre-comprarse extras (status="free+paid")
//  3. No vale la pena pre-comprar (status="onsite" o "free")
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Check, AlertCircle, Ticket as TicketIcon, ExternalLink, Zap, Info, Wallet, FileText, Image as ImageIcon, Calendar } from "lucide-react";
import { useMemo, useState } from "react";
import { TRIP } from "../data/trip";
import { useTickets } from "../hooks/useTickets";
import { useTripBase } from "../hooks/useTripBase";
import { useTrip } from "../auth/TripContext";
import { TicketLightbox } from "../components/TicketViewer";
import { findDaysForPlace, formatDate } from "../data/helpers";

const SECTIONS = [
  {
    key: "advance",
    title: "Sí o sí comprar antes",
    subtitle: "Cupos limitados o filas largas. Reservar con tiempo.",
    icon: Zap,
    accent: "hsl(0 70% 50%)",
    bg: "hsl(0 70% 97%)",
    statuses: ["advance"],
  },
  {
    key: "maybe",
    title: "Puede convenir comprar antes",
    subtitle: "Algunas partes gratis, otras de pago. Decidir según ganas.",
    icon: Info,
    accent: "hsl(35 80% 45%)",
    bg: "hsl(40 95% 96%)",
    statuses: ["free+paid"],
  },
  {
    key: "later",
    title: "No vale la pena pre-comprar",
    subtitle: "Se paga en la puerta o es gratis. Sin estrés.",
    icon: Wallet,
    accent: "hsl(140 40% 35%)",
    bg: "hsl(140 35% 96%)",
    statuses: ["onsite", "free"],
  },
];

export default function Tickets() {
  const base = useTripBase();
  const { tickets, hasTicket } = useTickets();
  const { trip } = useTrip();
  // Use ONLY the current trip's places/tickets — never fall back to the legacy
  // Europe trip (that would leak old data into unrelated trips).
  const placesMap = useMemo(() => ((trip && trip.places) || {}), [trip]);
  const ticketsMap = useMemo(() => ((trip && trip.tickets) || {}), [trip]);
  const companionTickets = (trip && trip.companionTickets) || [];

  const grouped = useMemo(() => {
    const all = Object.entries(ticketsMap).map(([placeId, ticket]) => {
      const place = placesMap[placeId];
      if (!place) return null;
      return { placeId, place, ticket, purchased: hasTicket(placeId) };
    }).filter(Boolean);

    return SECTIONS.map((sec) => {
      const list = all.filter((it) => sec.statuses.includes(it.ticket.status));
      // dentro de "advance": pendientes primero, luego comprados
      list.sort((a, b) => {
        if (sec.key === "advance") {
          if (a.purchased !== b.purchased) return a.purchased ? 1 : -1;
        }
        // luego por ciudad para agrupar visualmente
        return (a.place.city || "").localeCompare(b.place.city || "");
      });
      return { ...sec, items: list };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickets, hasTicket]);

  const advance = grouped.find((g) => g.key === "advance");
  const purchasedCount = advance ? advance.items.filter((i) => i.purchased).length : 0;
  const advanceTotal = advance ? advance.items.length : 0;

  // Lista plana de TODOS los tickets guardados (incluyendo onsite/free si el usuario los guardó).
  // Filtra los sub-tickets de un bundle (su includedIn apunta al maestro) — el maestro ya los representa.
  const purchasedList = useMemo(() => {
    return Object.entries(ticketsMap)
      .map(([placeId, ticket]) => {
        const place = placesMap[placeId];
        if (!place) return null;
        if (!hasTicket(placeId)) return null;
        // Hide sub-tickets — they're rolled up into their master entry.
        if (ticket.includedIn) return null;
        return { placeId, place, ticket, saved: tickets[placeId] };
      })
      .filter(Boolean);
  }, [tickets, hasTicket, placesMap, ticketsMap]);

  const [showPurchased, setShowPurchased] = useState(false);
  const [lightboxFiles, setLightboxFiles] = useState(null);

  // Helper para normalizar archivos de un ticket guardado (legacy + nuevo schema)
  const filesFromTicket = (t) => {
    if (Array.isArray(t?.files) && t.files.length) {
      return t.files.map((f, i) => ({ src: f.data || f.data_b64 || f.src, label: f.label || `Archivo ${i + 1}` }));
    }
    const single = t?.pdf_b64 || t?.image_b64;
    return single ? [{ src: single, label: "Ticket" }] : [];
  };

  return (
    <div className="container-app pt-6 pb-28" data-testid="tickets-page">
      <Link to={base || "/"} className="inline-flex items-center gap-1 text-sm text-[hsl(var(--muted))] mb-4" data-testid="tickets-back">
        <ChevronLeft size={16} /> Inicio
      </Link>
      <h1 className="text-3xl font-bold leading-tight">Tickets y entradas</h1>
      <p className="text-sm text-[hsl(var(--muted))] mt-1 mb-5">Qué reservar antes, qué pagar al llegar, qué es gratis.</p>

      {/* Resumen — clickeable para abrir lista de comprados */}
      <button
        type="button"
        onClick={() => setShowPurchased((v) => !v)}
        className="card-paper p-4 mb-3 flex items-center gap-3 w-full text-left active:scale-[0.99] transition-transform"
        style={{ background: purchasedCount === advanceTotal ? "hsl(140 50% 95%)" : "hsl(40 95% 95%)" }}
        data-testid="tickets-summary"
        aria-expanded={showPurchased}
      >
        <TicketIcon size={20} className="shrink-0 text-[hsl(var(--accent))]" />
        <div className="flex-1">
          <p className="font-semibold text-sm">
            {purchasedCount} / {advanceTotal} tickets reservados
          </p>
          <p className="text-xs text-[hsl(var(--muted))]">
            {purchasedList.length > 0
              ? `Toca para ${showPurchased ? "ocultar" : "ver"} tus ${purchasedList.length} ticket${purchasedList.length === 1 ? "" : "s"} guardado${purchasedList.length === 1 ? "" : "s"}`
              : "Lugares que requieren reserva anticipada"}
          </p>
        </div>
        {purchasedList.length > 0 && (
          <ChevronRight
            size={18}
            className="text-[hsl(var(--muted))] shrink-0 transition-transform"
            style={{ transform: showPurchased ? "rotate(90deg)" : "rotate(0deg)" }}
          />
        )}
      </button>

      {/* Panel desplegable con tickets comprados */}
      {showPurchased && purchasedList.length > 0 && (
        <div className="mb-5 space-y-2.5" data-testid="tickets-purchased-panel">
          <p className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted))] font-semibold px-1">
            Mis tickets guardados <span className="lowercase font-normal">· toca para ver el QR</span>
          </p>
          {purchasedList.map(({ placeId, place, saved }) => {
            const files = filesFromTicket(saved);
            const hasFiles = files.length > 0;
            const firstSrc = files[0]?.src;
            const hasImg = firstSrc && firstSrc.startsWith("data:image/");
            const hasPdf = firstSrc && firstSrc.startsWith("data:application/pdf");
            return (
              <div key={placeId} className="card-paper p-3 flex items-center gap-3" data-testid={`tickets-purchased-${placeId}`}>
                <button
                  type="button"
                  onClick={() => hasFiles && setLightboxFiles(files)}
                  className="flex items-center gap-3 flex-1 min-w-0 text-left active:scale-[0.99] transition-transform"
                  data-testid={`tickets-purchased-open-${placeId}`}
                  aria-label={`Ver ticket de ${place.name}`}
                >
                  {hasImg ? (
                    <img src={firstSrc} alt="" className="w-14 h-14 object-cover rounded-lg border border-[hsl(var(--line))] shrink-0 bg-white" />
                  ) : hasPdf ? (
                    <div className="w-14 h-14 rounded-lg border border-[hsl(var(--line))] bg-[hsl(var(--bg-card))] flex items-center justify-center shrink-0">
                      <FileText size={22} className="text-[hsl(var(--accent))]" />
                    </div>
                  ) : (
                    <div className="w-14 h-14 rounded-lg border border-[hsl(var(--line))] bg-[hsl(var(--bg-card))] flex items-center justify-center shrink-0 text-2xl">
                      {place.icon || "🎫"}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm truncate">{place.name}</p>
                    <p className="text-xs text-[hsl(var(--muted))] truncate">{place.city}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="chip-success text-[10px]"><Check size={10} /> Reservado</span>
                      {files.length > 1 && (
                        <span className="text-[10px] text-[hsl(var(--accent))] font-semibold">
                          {files.length} archivos
                        </span>
                      )}
                      {files.length === 1 && hasPdf && <span className="text-[10px] text-[hsl(var(--muted))] inline-flex items-center gap-1"><FileText size={10} /> PDF</span>}
                      {files.length === 1 && hasImg && <span className="text-[10px] text-[hsl(var(--muted))] inline-flex items-center gap-1"><ImageIcon size={10} /> Imagen</span>}
                    </div>
                  </div>
                </button>
                <Link
                  to={`${base}/lugar/${placeId}`}
                  className="text-[11px] text-[hsl(var(--accent))] font-medium shrink-0 px-2 py-1 underline-offset-2 hover:underline"
                  data-testid={`tickets-purchased-detail-${placeId}`}
                >
                  Detalles
                </Link>
              </div>
            );
          })}
        </div>
      )}

      {/* Lightbox compartido para ver el QR rápido */}
      <TicketLightbox files={lightboxFiles} open={!!lightboxFiles} onClose={() => setLightboxFiles(null)} />

      {/* 🤫 Companion (Igal) activity tickets — PRIVATE, admin only */}
      {companionTickets.length > 0 && (
        <section className="mb-6" data-testid="companion-tickets-section">
          <div
            className="rounded-2xl px-4 py-3 mb-3 flex items-start gap-3"
            style={{ background: "hsl(270 60% 96%)", borderLeft: "3px solid hsl(270 60% 50%)" }}
          >
            <span className="text-xl shrink-0">🤫</span>
            <div className="flex-1">
              <h2 className="font-bold text-base leading-tight" style={{ color: "hsl(270 60% 35%)" }}>
                Sorpresa · Igal
              </h2>
              <p className="text-xs text-purple-900/70 mt-0.5">
                Tickets de actividades reservadas por Igal — solo tú los ves. Oculto en el link compartido.
              </p>
            </div>
          </div>
          <ul className="space-y-3">
            {companionTickets.map((tk) => (
              <li
                key={tk.id}
                className="card-paper p-4 relative border-2 border-purple-200 bg-purple-50/60"
                data-testid={`companion-ticket-${tk.id}`}
              >
                <div className="flex items-start gap-2 mb-2">
                  <span className="px-1.5 py-0.5 rounded-full bg-purple-600 text-white text-[9px] font-bold uppercase tracking-wider shrink-0 mt-0.5">
                    🤫 Igal
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-base leading-tight text-purple-950">
                      <span className="mr-1">{tk.icon}</span>{tk.name}
                    </p>
                    <p className="text-[11px] text-purple-700/70 mt-0.5">{tk.city} · {tk.provider}</p>
                  </div>
                </div>
                <div className="mt-2 space-y-1 text-[11px] text-purple-950">
                  {(tk.date || tk.validityStart) && (
                    <p>
                      <span className="text-purple-700/70">Fecha:</span>{" "}
                      <span className="font-semibold">
                        {tk.validityStart && tk.validityEnd
                          ? `Válido ${formatDate(tk.validityStart)} – ${formatDate(tk.validityEnd)}`
                          : formatDate(tk.date)}
                      </span>
                      {tk.time && <span className="ml-1">· {tk.time}</span>}
                    </p>
                  )}
                  {tk.bookingRef && (
                    <p>
                      <span className="text-purple-700/70">Reserva:</span>{" "}
                      <span className="font-mono font-semibold">{tk.bookingRef}</span>
                      {tk.providerRef && <span className="ml-1 font-mono text-purple-700/70">· {tk.providerRef}</span>}
                    </p>
                  )}
                  {tk.price && (
                    <p>
                      <span className="text-purple-700/70">Costo:</span> {tk.price}
                    </p>
                  )}
                  {tk.passengers && (
                    <p>
                      <span className="text-purple-700/70">A nombre de:</span> {tk.passengers}
                    </p>
                  )}
                  {tk.address && (
                    <p className="text-purple-900/80">📍 {tk.address}</p>
                  )}
                  {tk.validityNote && (
                    <p className="text-purple-900/70 italic">⏳ {tk.validityNote}</p>
                  )}
                </div>
                {tk.sites && tk.sites.length > 0 && (
                  <details className="mt-2 text-[11px]">
                    <summary className="cursor-pointer text-purple-700 font-semibold">
                      Ver {tk.sites.length} sitios incluidos
                    </summary>
                    <ul className="mt-1 ml-3 list-disc text-purple-900/80 space-y-0.5">
                      {tk.sites.map((s, idx) => (
                        <li key={idx}>{s}</li>
                      ))}
                    </ul>
                  </details>
                )}
                {tk.notes && (
                  <p className="mt-2 pt-2 border-t border-purple-200 text-[11px] text-purple-900/90 leading-relaxed">
                    📌 {tk.notes}
                  </p>
                )}
                <p className="mt-2 text-[10px] text-purple-700/70 italic">
                  🔒 Solo tú ves esto. Oculto en el link compartido.
                </p>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Free walking tours promo */}
      <Link to={`${base}/tours`} className="card-paper p-4 mb-6 flex items-center gap-3 active:scale-[0.98]" data-testid="tickets-tours-link">
        <span className="text-2xl shrink-0">🚶</span>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm">Free walking tours</p>
          <p className="text-xs text-[hsl(var(--muted))]">9 tours gratuitos (con propina). Budapest · Praga · Madrid</p>
        </div>
        <ChevronRight size={16} className="text-[hsl(var(--muted))] shrink-0" />
      </Link>

      {/* Secciones agrupadas por prioridad */}
      <div className="space-y-7">
        {grouped.map((section) => {
          if (section.items.length === 0) return null;
          const Icon = section.icon;
          return (
            <section key={section.key} data-testid={`tickets-section-${section.key}`}>
              <div
                className="rounded-2xl px-4 py-3 mb-3 flex items-start gap-3"
                style={{ background: section.bg, borderLeft: `3px solid ${section.accent}` }}
              >
                <Icon size={18} style={{ color: section.accent }} className="shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <h2 className="font-semibold text-sm" style={{ color: section.accent }}>
                    {section.title}
                    <span className="ml-2 text-xs font-normal text-[hsl(var(--muted))]">({section.items.length})</span>
                  </h2>
                  <p className="text-xs text-[hsl(var(--muted))] mt-0.5">{section.subtitle}</p>
                </div>
              </div>

              <ul className="space-y-2.5">
                {section.items.map(({ placeId, place, ticket, purchased }) => {
                  const planned = !purchased ? findDaysForPlace(placeId) : [];
                  const includedIn = ticket.includedIn;
                  const masterPlace = includedIn ? placesMap[includedIn] : null;
                  return (
                  <li key={placeId}>
                    {/* Banner "Comprar para el ..." sólo en tickets pendientes que están en agenda Y no son parte de un bundle */}
                    {!purchased && !includedIn && planned.length > 0 && (
                      <div
                        className="-mb-2 mx-1 px-3 py-1.5 rounded-t-xl text-[11px] font-semibold flex items-center gap-1.5"
                        style={{ background: section.accent, color: "white" }}
                        data-testid={`ticket-day-hint-${placeId}`}
                      >
                        <Calendar size={11} className="shrink-0" />
                        {planned.length === 1
                          ? `Comprar para el ${formatDate(planned[0].date)} · ${planned[0].dayLabel}`
                          : `Comprar para el ${formatDate(planned[0].date)} (y ${planned.length - 1} día${planned.length > 2 ? "s" : ""} más)`}
                      </div>
                    )}
                    <Link
                      to={`${base}/lugar/${placeId}`}
                      className="card-paper p-4 block active:scale-[0.98] transition-transform"
                      data-testid={`ticket-row-${placeId}`}
                    >
                      <div className="flex items-start gap-3">
                        <span className="text-2xl shrink-0">{place.icon}</span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            {/* Si está en bundle, prioridad al chip "Incluido en..." */}
                            {includedIn && (
                              <span className="chip-ink text-[10px]" data-testid={`ticket-bundle-${placeId}`}>
                                <Check size={10} /> {purchased ? "Cubierto" : "Incluido en otro ticket"}
                              </span>
                            )}
                            {!includedIn && ticket.status === "advance" && !purchased && (
                              <span className="chip-warn"><AlertCircle size={10} /> Reservar antes</span>
                            )}
                            {!includedIn && ticket.status === "advance" && purchased && (
                              <span className="chip-success"><Check size={10} /> Reservado</span>
                            )}
                            {!includedIn && ticket.status === "onsite" && (
                              <span className="chip-ink">Se paga en la puerta</span>
                            )}
                            {!includedIn && ticket.status === "free" && (
                              <span className="chip-success">Gratis</span>
                            )}
                            {!includedIn && ticket.status === "free+paid" && (
                              <span className="chip-ink">Mixto</span>
                            )}
                            {!includedIn && purchased && ticket.status !== "advance" && (
                              <span className="chip-success"><Check size={10} /> Pagado</span>
                            )}
                          </div>
                          <p className="font-semibold text-[hsl(var(--ink))]">{place.name}</p>
                          <p className="text-xs text-[hsl(var(--muted))]">{place.city}</p>
                          {includedIn && masterPlace && (
                            <p className="text-[11px] text-[hsl(var(--accent))] mt-1">
                              📎 Incluido con: <span className="font-semibold">{masterPlace.name}</span>
                            </p>
                          )}
                          {!includedIn && ticket.price && <p className="text-xs text-[hsl(var(--accent))] font-medium mt-1">{ticket.price}</p>}
                          {!includedIn && ticket.url && (
                            <p className="text-[11px] text-[hsl(var(--muted))] mt-1 flex items-center gap-1 truncate">
                              <ExternalLink size={10} /> {ticket.url}
                            </p>
                          )}
                        </div>
                        <ChevronRight size={16} className="text-[hsl(var(--muted))] shrink-0 mt-1" />
                      </div>
                    </Link>
                  </li>
                  );
                })}
              </ul>
            </section>
          );
        })}
      </div>

      <p className="text-xs text-[hsl(var(--muted))] text-center mt-8 leading-relaxed">
        Toca cada lugar para ver más detalle, comprar online y guardar tu ticket en la app.
      </p>
    </div>
  );
}
